import autobind from 'autobind-decorator';
import * as React from 'react';
import { connect } from 'react-redux';
import { AnyAction, Dispatch } from 'redux';
import { H5, Icons, RectangleButton, Switcher, Text } from '@shared/ui-components';
import { RenderIf } from 'common/components/render-if';
import { KreoDialogActions } from 'common/UIKit';

import { EstimateSubscriptionError } from 'unit-subscription/interfaces/estimate-subscription-error';
import { EstimateSubscriptionModel } from 'unit-subscription/interfaces/estimation-subscription-model';
import { ConfirmationDialog } from '../../../../components/dialog/confirmation-dialog';
import { BillingPeriodUnit } from '../../enums/billing-period-unit';
import { BillingPricingModel } from '../../enums/billing-pricing-model';
import { SubscriptionAddon } from '../../interfaces/subscription-addon';
import { SubscriptionDetailsModel } from '../../interfaces/subscription-details';
import { BillingEstimationUtils } from '../../utils/billing-estimation-utils';
import { PaymentDetailsBlock } from '../payment-details-block';
import { PaymentMethodManager } from '../payment-method-manager';
import { AddonList } from './addon-list';
import { Coupons } from './coupons';
import { Styled } from './styled';
import { SubscriptionPrice } from './subscription-price';
import { TeamSize } from './team-size';


interface DispatchProps {
  openConfirmDialog: () => void;
}

interface Props extends DispatchProps {
  onSubscriptionChange?: (subscription: SubscriptionDetailsModel) => void;
  onApplyClick?: () => void;
  onChangePlanClick?: () => void;
  onCancelSubscriptionClick?: () => void;
  onChangePaymentCardId: (id: string) => void;
  onCountryIdChanged: (id: string) => void;
  onVatNumberIdChanged: (vatNumber: string) => void;
  header: string;
  billingEstimation: EstimateSubscriptionModel;
  applyButtonCaption: string;
  estimationError: EstimateSubscriptionError;
  currentSubscription: SubscriptionDetailsModel;
  currentTeamSize: number;
  minTeamSize: number;
  addons: SubscriptionAddon[];
  paymentCardId: string | null;
  countryId: string | null;
  vatNumber: string | null;
}

const CONFIRM_DELETE_SUBSCRIPTION_DIALOG = 'CONFIRM_DELETE_SUBSCRIPTION_DIALOG';
const CANCEL_SUBSCRIPTION_WARNING_FREE_PERIOD = `
⚠️ If you're still in your free trial period and cancel your license now,
you won't be able to continue the trial.`;
const CANCEL_SUBSCRIPTION_WARNING = `
⚠️ If you have an active subscription and want to cancel now,
you won't be able to access the platform after cancellation.`;

class SubscriptionDetailsComponent extends React.Component<Props> {
  public render(): JSX.Element {
    const { header, currentSubscription, addons, billingEstimation } = this.props;
    const planVariant = currentSubscription.planVariant;
    const isTeamPlan = planVariant
      && planVariant.pricingModel === BillingPricingModel.PerUnit
      && !planVariant.isCollaborationBlocked;
    const availableAddons = addons.filter(addon => planVariant.availableAddons.includes(addon.id));
    const isSubscriptionEditable = !!this.props.onSubscriptionChange;

    return (
      <Styled.Container>
        <Styled.ReviewCard>
          <H5>{header}</H5>
          {
            isTeamPlan && (
              <TeamSize
                currentTeamSize={this.props.currentTeamSize}
                minTeamSize={this.props.minTeamSize}
                onTeamSizeChange={this.onTeamSizeChanged}
              />
            )
          }
          <RenderIf condition={isSubscriptionEditable}>
            <AddonList
              availableAddons={availableAddons}
              onSelectedAddonIdsChange={this.onSelectedAddonIdsChange}
              selectedAddonIds={currentSubscription.selectedAddonIds}
              isTeamPlan={isTeamPlan}
              billingPeriod={currentSubscription.billingPeriod}
            />
          </RenderIf>
          <PaymentMethodManager
            setSubscriptionCard={this.props.onChangePaymentCardId}
            subscriptionCardId={this.props.paymentCardId}
            countryId={this.props.countryId}
            vatNumber={this.props.vatNumber}
            setCountryId={this.props.onCountryIdChanged}
            setVatNumber={this.props.onVatNumberIdChanged}
          />
        </Styled.ReviewCard>
        <Styled.PlanCard>
          <SubscriptionPrice
            currentSubscription={currentSubscription}
            addons={availableAddons}
            isViewMode={true}
            extraCurrency={BillingEstimationUtils.getExtraCurrency(this.props.countryId)}
          />
          <RenderIf condition={isSubscriptionEditable}>
            <Styled.Period>
              <Text fontSize={12} color='additionalFont'>Annually</Text>
              <Switcher
                checked={currentSubscription.planVariant.billingPeriodUnit === BillingPeriodUnit.Month}
                onChange={this.changePeriod}
                size='l'
              />
              <Text fontSize={12} color='additionalFont'>Monthly</Text>
            </Styled.Period>
          </RenderIf>
          <RectangleButton
            size='l'
            mood={this.props.onApplyClick ? 'secondary' : 'disabled'}
            onClick={this.props.onApplyClick}
            text={this.props.applyButtonCaption}
          />
          <RenderIf condition={isSubscriptionEditable}>
            <Coupons
              error={this.props.estimationError}
              coupons={this.props.currentSubscription.coupons}
              addCoupons={this.addCoupons}
            />
          </RenderIf>
          <RenderIf condition={!!this.props.currentSubscription || !!this.props.billingEstimation}>
            <PaymentDetailsBlock
              billingEstimation={billingEstimation}
            />
          </RenderIf>
          {this.props.onCancelSubscriptionClick && (
            <Text>
              <Styled.LinkButton
                color='red'
                id='cancel-subscription-btn'
                onClick={this.props.onCancelSubscriptionClick}
              >
                <Icons.Close />
                Cancel Subscription
              </Styled.LinkButton>
            </Text>
          )}
          {this.props.onChangePlanClick && (
            <Styled.LinkButton
              color='additionalFont'
              onClick={this.props.onChangePlanClick}
            >
              <Icons.LinkArrow />
              Change plan
            </Styled.LinkButton>
          )}
        </Styled.PlanCard>
        <ConfirmationDialog
          name={CONFIRM_DELETE_SUBSCRIPTION_DIALOG}
          title='Cancel Subscription'
          text={CANCEL_SUBSCRIPTION_WARNING_FREE_PERIOD}
          cancelButtonText='No, Go Back!'
          confirmButtonText='Yes'
          onConfirm={this.props.onCancelSubscriptionClick}
        >
          <Styled.CancelSubscriptionWarning>
            {CANCEL_SUBSCRIPTION_WARNING}
          </Styled.CancelSubscriptionWarning>
        </ConfirmationDialog>
      </Styled.Container>
    );
  }

  private sliceLastCoupon(coupons: string[]): string[] {
    return coupons.slice(coupons.length - 1, coupons.length);
  }

  @autobind
  private addCoupons(coupons: string[]): void {
    this.props.onSubscriptionChange({
      ...this.props.currentSubscription,
      coupons: this.sliceLastCoupon(coupons),
    });
  }


  @autobind
  private changePeriod(): void {
    const billingPeriod = this.props.currentSubscription.planVariant.billingPeriodUnit === BillingPeriodUnit.Month
      ? BillingPeriodUnit.Year
      : BillingPeriodUnit.Month;

    this.props.onSubscriptionChange({
      ...this.props.currentSubscription,
      billingPeriod,
    });
  }

  @autobind
  private onTeamSizeChanged(teamSize: number): void {
    this.props.onSubscriptionChange({
      ...this.props.currentSubscription,
      teamSize,
    });
  }

  @autobind
  private onSelectedAddonIdsChange(selectedAddonIds: string[]): void {
    this.props.onSubscriptionChange({
      ...this.props.currentSubscription,
      selectedAddonIds,
    });
  }
}


function mapDispatchToProps(dispatch: Dispatch<AnyAction>): DispatchProps {
  return {
    openConfirmDialog: () => dispatch(KreoDialogActions.openDialog(CONFIRM_DELETE_SUBSCRIPTION_DIALOG)),
  };
}

export const SubscriptionDetails = connect(null, mapDispatchToProps)(SubscriptionDetailsComponent);
